"use client";

import { useMemo } from "react";
import { useBinanceKline } from "@/hooks/useBinanceWebSocket";
import {
  computeFearGreed,
  computeBuyZones,
  type OHLCV,
} from "@/lib/indicators";

interface TokenSignalsProps {
  symbol: string;
  tokenName: string;
  currentPrice?: number | null;
}

function formatPrice(price: number): string {
  if (price < 0.0001) return price.toFixed(8);
  if (price < 0.01) return price.toFixed(6);
  if (price < 1) return price.toFixed(4);
  if (price < 100) return price.toFixed(3);
  return price.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function TokenSignals({
  symbol,
  tokenName,
  currentPrice,
}: TokenSignalsProps) {
  // Pull daily candles for signal computation
  const { klines, error } = useBinanceKline(symbol, "1d");
  const notAvailable = error === "not_available";

  const { fearGreed, buyZones, ohlcvLength } = useMemo(() => {
    if (klines.length < 30) {
      return { fearGreed: null, buyZones: [], ohlcvLength: klines.length };
    }
    const ohlcv: OHLCV[] = klines.map((k) => ({
      time: k.time,
      open: k.open,
      high: k.high,
      low: k.low,
      close: k.close,
      volume: k.volume,
    }));
    return {
      fearGreed: computeFearGreed(ohlcv),
      buyZones: computeBuyZones(ohlcv),
      ohlcvLength: ohlcv.length,
    };
  }, [klines]);

  if (notAvailable) {
    return (
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(230,15%,8%)] p-6">
        <h3 className="text-lg font-semibold text-white mb-2">
          Market Signals
        </h3>
        <p className="text-sm text-gray-500">
          Signals require Binance spot data — not available for {tokenName}.
        </p>
      </div>
    );
  }

  if (!fearGreed || ohlcvLength < 30) {
    return (
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(230,15%,8%)] p-6 animate-pulse">
        <div className="h-6 bg-[hsl(230,15%,14%)] rounded w-1/3 mb-4" />
        <div className="h-40 bg-[hsl(230,15%,10%)] rounded" />
      </div>
    );
  }

  // Build the gauge arc
  const gaugeAngle = (fearGreed.score / 100) * 180 - 90; // -90 to +90 deg
  const px = currentPrice ?? klines[klines.length - 1]?.close ?? 0;

  return (
    <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(230,15%,8%)] overflow-hidden">
      <div className="p-6 border-b border-[hsl(var(--border))]">
        <h3 className="text-lg font-semibold text-white mb-1">Market Signals</h3>
        <p className="text-xs text-gray-500">
          Computed from {ohlcvLength} days of {tokenName} price action
        </p>
      </div>

      {/* Fear & Greed Gauge */}
      <div className="p-6 border-b border-[hsl(var(--border))]">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">
            Fear & Greed Index
          </h4>
          <span
            className="text-xs font-bold px-2.5 py-1 rounded-full"
            style={{
              backgroundColor: `${fearGreed.color}22`,
              color: fearGreed.color,
              borderColor: `${fearGreed.color}55`,
              borderWidth: 1,
              borderStyle: "solid",
            }}
          >
            {fearGreed.label}
          </span>
        </div>

        {/* SVG gauge */}
        <div className="flex items-center justify-center my-4">
          <svg viewBox="0 0 200 120" className="w-full max-w-xs">
            <defs>
              <linearGradient id="fg-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#dc2626" />
                <stop offset="25%" stopColor="#f97316" />
                <stop offset="50%" stopColor="#eab308" />
                <stop offset="75%" stopColor="#84cc16" />
                <stop offset="100%" stopColor="#22c55e" />
              </linearGradient>
            </defs>
            {/* Arc */}
            <path
              d="M 20 100 A 80 80 0 0 1 180 100"
              fill="none"
              stroke="url(#fg-gradient)"
              strokeWidth="16"
              strokeLinecap="round"
            />
            {/* Needle */}
            <g transform={`rotate(${gaugeAngle} 100 100)`}>
              <line
                x1="100"
                y1="100"
                x2="100"
                y2="28"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
              />
              <circle cx="100" cy="100" r="6" fill="white" />
            </g>
            {/* Score text */}
            <text
              x="100"
              y="90"
              textAnchor="middle"
              fontSize="28"
              fontWeight="bold"
              fill="white"
              fontFamily="monospace"
            >
              {fearGreed.score}
            </text>
            <text
              x="100"
              y="110"
              textAnchor="middle"
              fontSize="9"
              fill="#9ca3af"
            >
              / 100
            </text>
          </svg>
        </div>

        {/* Component breakdown */}
        <div className="grid grid-cols-5 gap-2 mt-4">
          {[
            { label: "RSI", value: fearGreed.components.rsi, weight: "25%" },
            { label: "Momentum", value: fearGreed.components.momentum, weight: "20%" },
            { label: "Volatility", value: fearGreed.components.volatility, weight: "20%" },
            { label: "Volume", value: fearGreed.components.volumeTrend, weight: "15%" },
            { label: "Drawdown", value: fearGreed.components.drawdown, weight: "20%" },
          ].map((c) => (
            <div
              key={c.label}
              className="bg-[hsl(230,15%,10%)] rounded-lg p-2 text-center"
            >
              <p className="text-[9px] text-gray-500 uppercase tracking-wide">
                {c.label}
              </p>
              <p className="text-sm font-bold font-mono text-white mt-0.5">
                {c.value}
              </p>
              <div className="h-0.5 bg-[hsl(230,15%,16%)] rounded-full mt-1 overflow-hidden">
                <div
                  className="h-full transition-all"
                  style={{
                    width: `${c.value}%`,
                    backgroundColor:
                      c.value <= 20
                        ? "#dc2626"
                        : c.value <= 40
                          ? "#f97316"
                          : c.value <= 60
                            ? "#eab308"
                            : c.value <= 80
                              ? "#84cc16"
                              : "#22c55e",
                  }}
                />
              </div>
              <p className="text-[8px] text-gray-600 mt-0.5">{c.weight}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Optimal Buy Zones */}
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">
            Optimal Buy Zones
          </h4>
          <span className="text-xs text-gray-500 font-mono">
            Current: ${formatPrice(px)}
          </span>
        </div>

        {buyZones.length === 0 ? (
          <p className="text-sm text-gray-500 py-4 text-center">
            No high-confidence buy zones detected. Market conditions do not
            currently favor a low-risk entry — wait for RSI or Bollinger Band
            signals.
          </p>
        ) : (
          <div className="space-y-3">
            {buyZones.map((zone, i) => {
              const distancePct = ((px - zone.priceHigh) / px) * 100;
              const isInZone = px >= zone.priceLow && px <= zone.priceHigh;
              return (
                <div
                  key={i}
                  className={`rounded-lg border p-4 ${
                    isInZone
                      ? "bg-emerald-900/20 border-emerald-700/50"
                      : "bg-[hsl(230,15%,10%)] border-[hsl(230,15%,18%)]"
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-gray-400">
                          Zone #{i + 1}
                        </span>
                        {isInZone && (
                          <span className="text-[10px] font-bold bg-emerald-600 text-white px-2 py-0.5 rounded-full">
                            PRICE IN ZONE
                          </span>
                        )}
                      </div>
                      <p className="text-lg font-bold text-white font-mono mt-1">
                        ${formatPrice(zone.priceLow)} – ${formatPrice(zone.priceHigh)}
                      </p>
                      {!isInZone && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {distancePct > 0
                            ? `${distancePct.toFixed(1)}% below current price`
                            : `${Math.abs(distancePct).toFixed(1)}% above current`}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                        Confidence
                      </p>
                      <p
                        className="text-2xl font-bold font-mono"
                        style={{
                          color:
                            zone.confidence >= 75
                              ? "#22c55e"
                              : zone.confidence >= 60
                                ? "#eab308"
                                : "#94a3b8",
                        }}
                      >
                        {zone.confidence}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {zone.reasons.map((r, ri) => (
                      <span
                        key={ri}
                        className="text-[10px] px-2 py-0.5 rounded-full bg-[hsl(230,15%,14%)] text-gray-400 border border-[hsl(230,15%,20%)]"
                      >
                        {r}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="px-6 py-3 border-t border-[hsl(var(--border))] bg-[hsl(230,15%,6%)] text-[10px] text-gray-500">
        <strong className="text-gray-400">Disclaimer:</strong> Signals are
        computed from public market data and are not financial advice. Always do
        your own research before trading.
      </div>
    </div>
  );
}
